import * as React from "react";
import styled from "styled-components";
import defaultTheme from "../defaultTheme";
import Button from "../Button";
import ButtonLink, { StyledButtonLink } from "../ButtonLink";
import FormLabel from "../FormLabel";
import FormFeedback from "../FormFeedback";
import Attachment from "../icons/Attachment";
import CloseCircle from "../icons/CloseCircle";
import { rtlSpacing } from "../utils/rtl";
import getSpacingToken from "../common/getSpacingToken";
import getFieldDataState from "../common/getFieldDataState";
var Field = styled.label.withConfig({
  displayName: "InputFile__Field",
  componentId: "sc-12oq9bq-0"
})(["font-family:", ";display:block;position:relative;width:100%;margin-bottom:", ";"], function (_ref) {
  var theme = _ref.theme;
  return theme.orbit.fontfamily;
}, getSpacingToken);
Field.defaultProps = {
  theme: defaultTheme
};
var FakeInput = styled(function (_ref2) {
  var children = _ref2.children,
      className = _ref2.className;
  return React.createElement("div", {
    className: className
  }, children);
}).withConfig({
  displayName: "InputFile__FakeInput",
  componentId: "sc-12oq9bq-1"
})(["box-sizing:border-box;display:flex;align-items:center;padding:", ";height:", ";border-radius:", ";box-shadow:inset 0 0 0 ", ";background-color:", ";transition:box-shadow ", " ease-in-out;&:hover{box-shadow:inset 0 0 0 ", ";}", ":active{box-shadow:none;}"], function (_ref3) {
  var theme = _ref3.theme;
  return rtlSpacing(theme.orbit.paddingInputFile);
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.orbit.heightInputNormal;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.orbit.borderRadiusNormal;
}, function (_ref6) {
  var theme = _ref6.theme,
      error = _ref6.error;
  return "".concat(theme.orbit.borderWidthInput, " ").concat(error ? theme.orbit.borderColorInputError : theme.orbit.borderColorInput);
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.backgroundInput;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.orbit.durationFast;
}, function (_ref9) {
  var theme = _ref9.theme,
      error = _ref9.error;
  return "".concat(theme.orbit.borderWidthInput, " ").concat(error ? theme.orbit.paletteRedNormalHover : theme.orbit.borderColorInputHover);
}, StyledButtonLink);
FakeInput.defaultProps = {
  theme: defaultTheme
};
var Input = styled.input.withConfig({
  displayName: "InputFile__Input",
  componentId: "sc-12oq9bq-2"
})(["opacity:0;position:absolute;height:0;&:focus ~ ", "{box-shadow:", ";}"], FakeInput, function (_ref10) {
  var theme = _ref10.theme;
  return "inset 0 0 0 ".concat(theme.orbit.borderWidthInputFocus, " ").concat(theme.orbit.borderColorInputFocus);
});
Input.defaultProps = {
  theme: defaultTheme
};

var getFileInputColor = function getFileInputColor(_ref11, theme) {
  var error = _ref11.error,
      fileName = _ref11.fileName;

  if (error) {
    return theme.orbit.paletteRedNormal;
  }

  if (fileName) {
    return theme.orbit.colorTextInput;
  }

  return theme.orbit.paletteInkLight;
};

var StyledFileInput = styled.div.withConfig({
  displayName: "InputFile__StyledFileInput",
  componentId: "sc-12oq9bq-3"
})(["font-family:", ";color:", ";width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:", ";"], function (_ref12) {
  var theme = _ref12.theme;
  return theme.orbit.fontFamily;
}, function (_ref13) {
  var error = _ref13.error,
      fileName = _ref13.fileName,
      theme = _ref13.theme;
  return getFileInputColor({
    error: error,
    fileName: fileName
  }, theme);
}, function (_ref14) {
  var theme = _ref14.theme;
  return rtlSpacing("0 0 0 ".concat(theme.orbit.spaceSmall));
});
StyledFileInput.defaultProps = {
  theme: defaultTheme
};
var InputButton = styled(Button).withConfig({
  displayName: "InputFile__InputButton",
  componentId: "sc-12oq9bq-4"
})(["flex-shrink:0;"]);
var CloseButton = styled(ButtonLink).withConfig({
  displayName: "InputFile__CloseButton",
  componentId: "sc-12oq9bq-5"
})(["flex-shrink:0;& svg{color:", ";}"], function (_ref15) {
  var theme = _ref15.theme;
  return theme.orbit.paletteInkLight;
});
CloseButton.defaultProps = {
  theme: defaultTheme
}; // $FlowExpected

var InputFile = React.forwardRef(function (props, ref) {
  var _props$placeholder = props.placeholder,
      placeholder = _props$placeholder === void 0 ? "No file selected" : _props$placeholder,
      _props$buttonLabel = props.buttonLabel,
      buttonLabel = _props$buttonLabel === void 0 ? "Select file" : _props$buttonLabel,
      onRemoveFile = props.onRemoveFile,
      dataTest = props.dataTest,
      spaceAfter = props.spaceAfter;
  return React.createElement(Field, {
    spaceAfter: spaceAfter
  }, React.createElement(Input, {
    "data-test": dataTest,
    "data-state": getFieldDataState(!!props.error),
    type: "file",
    name: props.name,
    error: props.error,
    onChange: props.onChange,
    onFocus: props.onChange,
    onBlur: props.onBlur,
    accept: props.allowedFileTypes,
    ref: ref,
    tabIndex: props.tabIndex
  }), props.label && React.createElement(FormLabel, {
    filled: !!props.fileName
  }, props.label), React.createElement(FakeInput, {
    error: props.error
  }, React.createElement(InputButton, {
    type: "secondary",
    size: "small",
    icon: React.createElement(Attachment, null),
    component: "div"
  }, buttonLabel), React.createElement(StyledFileInput, {
    fileName: props.fileName,
    error: props.error
  }, props.fileName || placeholder), props.fileName && React.createElement(CloseButton, {
    type: "secondary",
    transparent: true,
    icon: React.createElement(CloseCircle, null),
    onClick: function onClick(ev) {
      ev.preventDefault();

      if (onRemoveFile) {
        onRemoveFile();
      }
    }
  })), props.help && !props.error && React.createElement(FormFeedback, {
    type: "help"
  }, props.help), props.error && React.createElement(FormFeedback, {
    type: "error"
  }, props.error));
});
InputFile.displayName = "InputFile";
export default InputFile;